import type { InvoiceDetail } from './types';

import {
  type QueryFunctionContext,
  queryOptions,
  skipToken,
  useQuery,
  useSuspenseQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ms } from 'ms';

import type { UUID } from '~/types/common';

import { rateLimitedResponse } from '~/services/queryClient';

import { queryKeysInvoice } from '../queryKeys';

import { fetchInvoiceDetail } from './fetchInvoiceDetail';

/**
 * Generates query options for the tenant-scoped invoice detail endpoint.
 *
 * @param invoiceId - Invoice identifier.
 * @param options - Additional query options.
 * @returns Query options for invoice detail.
 */
export function getInvoiceDetailQueryOptions({
  invoiceId,
  options,
}: {
  invoiceId: UUID | undefined;
  options?: Omit<UseQueryOptions<InvoiceDetail>, 'queryKey' | 'queryFn'>;
}) {
  return queryOptions({
    queryFn: invoiceId
      ? (context: QueryFunctionContext) =>
          fetchInvoiceDetail(invoiceId, context)
      : skipToken,
    queryKey: queryKeysInvoice.getDetail(invoiceId ?? ''),
    staleTime: ms('60s'),
    ...options,
    // A failed reload keeps the page on its cached invoice; only a first load
    // with nothing to show reaches the error boundary.
    throwOnError: (error, query) =>
      query.state.data === undefined && !rateLimitedResponse(error),
  });
}

/**
 * Loads invoice detail for the v2 invoice page.
 *
 * @param invoiceId - Invoice identifier.
 * @param options - Additional query options.
 * @returns TanStack Query result.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/get_invoice_invoices__invoice_id__get
 */
export function useQueryInvoiceDetail(
  invoiceId: UUID | undefined,
  options?: Omit<UseQueryOptions<InvoiceDetail>, 'queryKey' | 'queryFn'>,
) {
  return useQuery(getInvoiceDetailQueryOptions({ invoiceId, options }));
}

/**
 * Loads invoice detail and suspends until data is available.
 *
 * @param invoiceId - Invoice identifier.
 * @returns Suspense query result containing invoice detail.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/get_invoice_invoices__invoice_id__get
 */
export function useSuspenseQueryInvoiceDetail(invoiceId: UUID) {
  return useSuspenseQuery(getSuspenseInvoiceDetailQueryOptions({ invoiceId }));
}

function getSuspenseInvoiceDetailQueryOptions({
  invoiceId,
}: {
  invoiceId: UUID;
}) {
  return queryOptions({
    queryFn: (context: QueryFunctionContext) =>
      fetchInvoiceDetail(invoiceId, context),
    queryKey: queryKeysInvoice.getDetail(invoiceId),
    staleTime: ms('60s'),
  });
}
